import type { Participant } from '@crowdset/contracts';
import { Avatar } from '@crowdset/ui';
import {
  CaretRight,
  Crown,
  DotsThreeVertical,
  Headphones,
  ShieldCheck,
  WifiSlash,
} from '@phosphor-icons/react';
import { Link } from 'react-router';
import { useSession } from '../../lib/queries';
import { useRoomStore } from '../../room/store';
import { Badge, cx, EmptyState } from '../../ui/kit';
import { UserMenu } from './UserMenu';

export function QueuePanel() {
  const data = useRoomStore((s) => s.data)!;
  const session = useSession().data!;
  const { play, queue, me } = data;
  return (
    <div className="cs-scroll h-full overflow-y-auto p-4">
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-fg-subtle">
        In the booth
      </h3>
      {play ? (
        <div className="mb-5 flex items-center gap-3 rounded-xl border border-accent/25 bg-accent/[0.05] p-3">
          <div
            className="flex h-10 w-10 items-end justify-center overflow-hidden rounded-lg bg-ink-800"
            aria-hidden
          >
            <Avatar avatarId={play.dj.avatarId} size={38} state="dj" className="translate-y-3" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{play.dj.displayName}</p>
            <p className="truncate text-xs text-fg-muted">{play.title}</p>
          </div>
          {me.isCurrentDj && (
            <Badge tone="accent">
              {me.continuesAfterTurn ? 'Rejoins the tail' : 'Leaving after this'}
            </Badge>
          )}
        </div>
      ) : (
        <p className="mb-5 text-sm text-fg-muted">Nobody is DJing.</p>
      )}

      <h3 className="mb-2 flex items-center justify-between text-xs font-medium uppercase tracking-wider text-fg-subtle">
        Waitlist <span className="font-mono normal-case">{queue.length}</span>
      </h3>
      {queue.length === 0 ? (
        <EmptyState
          title="No one is waiting"
          body={
            session.user
              ? 'Join the queue with an active playlist. You play one track per turn, then move to the back.'
              : 'Sign in, build a playlist, and join the queue to play a track.'
          }
        />
      ) : (
        <ol className="flex flex-col gap-1" aria-label="DJ waitlist in order">
          {queue.map((q, i) => (
            <li
              key={q.user.id}
              className={cx(
                'flex items-center gap-3 rounded-xl px-2 py-1.5',
                q.user.id === me.userId
                  ? 'bg-accent/[0.08] ring-1 ring-inset ring-accent/25'
                  : 'hover:bg-white/[0.03]',
              )}
            >
              <span className="w-5 text-right font-mono text-xs text-fg-subtle">{i + 1}</span>
              <div
                className="flex h-8 w-8 items-end justify-center overflow-hidden rounded-lg bg-ink-800"
                aria-hidden
              >
                <Avatar avatarId={q.user.avatarId} size={30} className="translate-y-2" />
              </div>
              <span className="flex-1 truncate text-sm">
                {q.user.displayName}
                {q.user.id === me.userId && <span className="text-fg-subtle"> (you)</span>}
              </span>
              {!q.connected && (
                <span
                  className="flex items-center gap-1 text-[11px] text-warn"
                  title="Disconnected — kept for 30 seconds"
                >
                  <WifiSlash size={12} aria-hidden /> away
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
      <p className="mt-5 text-xs leading-relaxed text-fg-subtle">
        One track per turn. If your track cannot play (removed, too long, played in the last 30
        minutes), the next one in your playlist is tried.
      </p>
    </div>
  );
}

const ROLE_ORDER: Record<string, number> = { owner: 0, moderator: 1, member: 2, guest: 3 };

export function PeoplePanel() {
  const data = useRoomStore((s) => s.data)!;
  const people = [...data.participants].sort(
    (a, b) =>
      (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) ||
      a.displayName.localeCompare(b.displayName),
  );
  const guests = people.filter((p) => p.isGuest).length;
  return (
    <div className="cs-scroll h-full overflow-y-auto p-4">
      <p className="mb-3 text-xs text-fg-subtle">
        <span className="font-mono text-fg">{people.length}</span> of {data.room.capacity} here
        {guests > 0 && ` · ${guests} guest${guests > 1 ? 's' : ''} listening`}
      </p>
      <ul className="flex flex-col gap-0.5" aria-label="People in this room">
        {people.map((p) => (
          <PersonRow key={p.participantId} p={p} />
        ))}
      </ul>
    </div>
  );
}

function PersonRow({ p }: { p: Participant }) {
  const data = useRoomStore((s) => s.data)!;
  const isDj = data.play?.dj.id === p.userId && !!p.userId;
  const isMe = p.participantId === data.me.participantId;
  return (
    <li>
      <UserMenu participant={p}>
        <button
          className={cx(
            'flex w-full items-center gap-3 rounded-xl px-2 py-1.5 text-left hover:bg-white/[0.04] focus-visible:bg-white/[0.04]',
            !p.connected && 'opacity-55',
          )}
          aria-label={`${p.displayName}${isMe ? ' (you)' : ''}, ${p.role}${isDj ? ', DJing' : ''}${!p.connected ? ', reconnecting' : ''}. Open actions`}
        >
          <div
            className="flex h-8 w-8 shrink-0 items-end justify-center overflow-hidden rounded-lg bg-ink-800"
            aria-hidden
          >
            <Avatar avatarId={p.avatarId} size={30} className="translate-y-2" />
          </div>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm">
              {p.displayName}
              {isMe && <span className="text-fg-subtle"> (you)</span>}
            </span>
            {p.handle && (
              <span className="block truncate font-mono text-[11px] text-fg-subtle">
                @{p.handle}
              </span>
            )}
          </span>
          {isDj && <Headphones size={14} className="text-accent" aria-hidden />}
          {p.role === 'owner' && <Crown size={14} className="text-woot" aria-hidden />}
          {p.role === 'moderator' && <ShieldCheck size={14} className="text-accent" aria-hidden />}
          {p.isGuest && <span className="text-[11px] text-fg-subtle">guest</span>}
          <DotsThreeVertical size={16} className="text-fg-subtle" aria-hidden />
        </button>
      </UserMenu>
    </li>
  );
}

export function LibraryLink() {
  return (
    <Link
      to="/library"
      className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
    >
      Open full library <CaretRight size={12} aria-hidden />
    </Link>
  );
}
